import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import { API_URL, formatCurrency, formatDate, getStatusColor, statusLabels } from "../../lib/utils";
import { Receipt } from "lucide-react";

export const PortalInvoicesPage = () => {
    const { token } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch_ = async () => {
            try {
                const res = await fetch(`${API_URL}/portal/invoices`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) setInvoices(await res.json());
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        fetch_();
    }, [token]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const totalOwed = invoices
        .filter((i) => i.status !== "paid" && i.status !== "cancelled")
        .reduce((sum, i) => sum + (i.total || 0), 0);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="portal-invoices-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl text-foreground">Mes factures</h1>
                    <p className="text-muted-foreground text-sm mt-1">Consultez l'état de vos factures</p>
                </div>
                {totalOwed > 0 && (
                    <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                        <CardContent className="p-3 px-4">
                            <p className="text-xs text-amber-700 dark:text-amber-400">Total en attente de paiement</p>
                            <p className="text-lg font-bold text-amber-800 dark:text-amber-300 font-mono">
                                {formatCurrency(totalOwed)}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>

            {invoices.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Receipt className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">Aucune facture pour le moment</p>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>N° Facture</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Échéance</TableHead>
                                <TableHead className="text-right">Montant</TableHead>
                                <TableHead>Statut</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {invoices.map((inv) => {
                                const isOverdue = inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== "paid";
                                return (
                                    <TableRow key={inv.invoice_id}>
                                        <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                                        <TableCell>{formatDate(inv.created_at)}</TableCell>
                                        <TableCell className={isOverdue ? "text-red-500 font-medium" : ""}>
                                            {inv.due_date ? formatDate(inv.due_date) : "-"}
                                        </TableCell>
                                        <TableCell className="text-right font-mono">{formatCurrency(inv.total)}</TableCell>
                                        <TableCell>
                                            <Badge className={getStatusColor(inv.status)}>
                                                {isOverdue ? "En retard" : (statusLabels[inv.status] || inv.status)}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </div>
    );
};
